import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import {
  Loader2, Smartphone, Monitor, RefreshCw, Trophy, AlertTriangle,
  Gavel, CalendarClock, Users, ChevronRight, X, Check, ExternalLink,
} from "lucide-react";

/**
 * Admin view for the EA FC Pro Clubs game mode.
 *
 * Tabs:
 *  - Leagues: every season per pool, with team/fixture counts. Drill-down
 *    opens a league detail panel with rosters + fixture grid + standings.
 *  - Disputes: cross-league disputed fixtures with inline override modal.
 *  - All Fixtures: searchable fixture browser (filter by status, league).
 *
 * Admin actions:
 *  - Override a fixture score (forces a final result, marks admin_override)
 *  - Force-reschedule a fixture
 *  - Change a league's status (recruiting / active / completed)
 */
export default function AdminProClubsTab() {
  const [tab, setTab] = useState("leagues");

  return (
    <div className="space-y-6" data-testid="admin-proclubs-root">
      <SectionTitle kicker="// admin control" testId="admin-proclubs-title">
        Pro Clubs Administration
      </SectionTitle>

      <div className="flex gap-1 border-b border-[#E4E4E7] dark:border-[#2A2A30]" data-testid="admin-proclubs-tabs">
        <SubTabBtn id="leagues"  cur={tab} setCur={setTab} icon={Trophy}        label="Leagues" />
        <SubTabBtn id="disputes" cur={tab} setCur={setTab} icon={AlertTriangle} label="Disputes" />
        <SubTabBtn id="fixtures" cur={tab} setCur={setTab} icon={CalendarClock} label="Fixtures" />
      </div>

      {tab === "leagues"  && <LeaguesView />}
      {tab === "disputes" && <DisputesView />}
      {tab === "fixtures" && <FixturesView />}
    </div>
  );
}

function SubTabBtn({ id, cur, setCur, icon: Icon, label }) {
  const active = cur === id;
  return (
    <button
      type="button"
      data-testid={`admin-proclubs-tab-${id}`}
      onClick={() => setCur(id)}
      className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
        active
          ? "border-[#00FF87] text-[#00FF87]"
          : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

/* =========================================================================
   LEAGUES VIEW — list every season, drill-down for full detail
   ========================================================================= */
function LeaguesView() {
  const [leagues, setLeagues] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const { data } = await api.get("/proclubs/admin/leagues");
      setLeagues(data?.leagues || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (leagues === null) return <TactLoader />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-[#71717A]">
          {leagues.length} season{leagues.length !== 1 ? "s" : ""} across both pools
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="admin-proclubs-leagues-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#1F1F2A] text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 text-[#A1A1AA] hover:text-white transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      {leagues.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO PRO CLUBS LEAGUES YET. ]</Panel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-proclubs-leagues-list">
          {leagues.map((L) => (
            <LeagueRow key={L.id} L={L} onOpen={() => setSelected(L)} />
          ))}
        </div>
      )}

      {selected && (
        <LeagueDetailModal
          league={selected}
          onClose={() => setSelected(null)}
          onChanged={() => load(true)}
        />
      )}
    </div>
  );
}

function LeagueRow({ L, onOpen }) {
  const PoolIcon = L.pool === "console" ? Monitor : Smartphone;
  const statusColor = {
    recruiting: "#F59E0B",
    active:     "#00FF87",
    completed:  "#71717A",
  }[L.status] || "#A1A1AA";
  return (
    <button
      type="button"
      data-testid={`admin-proclubs-league-${L.id}`}
      onClick={onOpen}
      className="w-full text-left bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#00FF87] rounded-2xl p-4 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <PoolIcon className="w-4 h-4 text-[#A1A1AA] flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-[#09090B] dark:text-white truncate">
              {(L.pool || "mobile").toUpperCase()} · Season {L.season_no}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
              {L.id.slice(0,8)}…
            </div>
          </div>
        </div>
        <span
          className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
          style={{ backgroundColor: `${statusColor}1A`, color: statusColor, border: `1px solid ${statusColor}55` }}
          data-testid={`admin-proclubs-league-${L.id}-status`}
        >
          {L.status}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        <Stat label="Teams" value={L.team_count} />
        <Stat label="Sched" value={L.fixture_counts?.scheduled} />
        <Stat label="Done"  value={L.fixture_counts?.confirmed} accent="#00FF87" />
        <Stat label="Disp"  value={L.fixture_counts?.disputed}  accent={L.fixture_counts?.disputed ? "#E11D48" : undefined} />
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">
        Open detail <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] px-2 py-1.5 text-center">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="text-lg font-bold tabular-nums" style={{ color: accent || undefined }}>
        {value ?? 0}
      </div>
    </div>
  );
}

/* =========================================================================
   LEAGUE DETAIL MODAL — full snapshot of one league
   ========================================================================= */
function LeagueDetailModal({ league, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("standings");
  const [overrideFx, setOverrideFx] = useState(null);
  const [rescheduleFx, setRescheduleFx] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/proclubs/admin/leagues/${league.id}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [league.id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    if (!window.confirm(`Force this league to status "${status}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/proclubs/admin/leagues/${league.id}/status`, { status });
      toast.success(`League status set to ${status}.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-proclubs-league-detail-modal">
      <div className="bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-2xl w-full max-w-5xl my-4">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7] dark:border-[#2A2A30] sticky top-0 bg-white dark:bg-[#12121C] z-10 rounded-t-2xl">
          <div>
            <h3 className="text-sm font-bold text-[#09090B] dark:text-white">
              {(league.pool || "mobile").toUpperCase()} · Season {league.season_no}
            </h3>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mt-0.5">
              {league.status} · {league.team_count} teams · {league.fixture_counts?.total || 0} fixtures
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              defaultValue=""
              onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) setStatus(v); }}
              disabled={busy}
              data-testid="admin-proclubs-league-status-select"
              className="text-[10px] font-mono uppercase tracking-widest bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] rounded-full px-3 py-1.5 text-[#09090B] dark:text-white"
            >
              <option value="">Set status…</option>
              <option value="recruiting">Recruiting</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-proclubs-league-detail-close"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#1F1F2A] hover:bg-[#1F1F2A]"
            >
              <X className="w-4 h-4 text-[#A1A1AA]" />
            </button>
          </div>
        </div>

        <div className="flex gap-1 border-b border-[#E4E4E7] dark:border-[#2A2A30] px-4">
          <SubTabBtn id="standings" cur={tab} setCur={setTab} icon={Trophy}        label="Standings" />
          <SubTabBtn id="teams"     cur={tab} setCur={setTab} icon={Users}         label="Teams" />
          <SubTabBtn id="fixtures"  cur={tab} setCur={setTab} icon={CalendarClock} label="Fixtures" />
        </div>

        <div className="p-4">
          {!data ? <TactLoader /> : (
            <>
              {tab === "standings" && <StandingsTable rows={data.standings} />}
              {tab === "teams"     && <TeamsList teams={data.teams} />}
              {tab === "fixtures"  && (
                <FixturesTable
                  fixtures={data.fixtures}
                  onOverride={(f) => setOverrideFx(f)}
                  onReschedule={(f) => setRescheduleFx(f)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {overrideFx && (
        <OverrideModal
          fixture={overrideFx}
          onClose={() => setOverrideFx(null)}
          onApplied={async () => { setOverrideFx(null); await load(); onChanged?.(); }}
        />
      )}
      {rescheduleFx && (
        <RescheduleModal
          fixture={rescheduleFx}
          onClose={() => setRescheduleFx(null)}
          onApplied={async () => { setRescheduleFx(null); await load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function StandingsTable({ rows }) {
  if (!rows?.length) return <Panel className="font-mono text-[#71717A] text-sm">[ NO STANDINGS YET ]</Panel>;
  return (
    <div className="overflow-x-auto" data-testid="admin-proclubs-standings">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30] text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
            <th className="text-left py-2 pr-2">#</th>
            <th className="text-left py-2">Team</th>
            <th className="text-center">P</th>
            <th className="text-center">W</th>
            <th className="text-center">D</th>
            <th className="text-center">L</th>
            <th className="text-center">GF</th>
            <th className="text-center">GA</th>
            <th className="text-center">GD</th>
            <th className="text-right pl-2">Pts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.team.id} className="border-b border-[#E4E4E7]/50 dark:border-[#1F1F2A]">
              <td className="py-2 pr-2 font-mono text-xs text-[#71717A]">{r.rank}</td>
              <td className="py-2 font-bold text-[#09090B] dark:text-white">{r.team.crest_emoji || "⚽"} {r.team.name}</td>
              <td className="text-center tabular-nums">{r.P}</td>
              <td className="text-center tabular-nums">{r.W}</td>
              <td className="text-center tabular-nums">{r.D}</td>
              <td className="text-center tabular-nums">{r.L}</td>
              <td className="text-center tabular-nums">{r.GF}</td>
              <td className="text-center tabular-nums">{r.GA}</td>
              <td className="text-center tabular-nums">{r.GD > 0 ? `+${r.GD}` : r.GD}</td>
              <td className="text-right pl-2 font-bold tabular-nums text-[#00FF87]">{r.Pts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamsList({ teams }) {
  if (!teams?.length) return <Panel className="font-mono text-[#71717A] text-sm">[ NO TEAMS YET ]</Panel>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-proclubs-teams">
      {teams.map((t) => (
        <div key={t.id} className="bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] rounded-xl p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold text-[#09090B] dark:text-white">
              {t.crest_emoji || "⚽"} {t.name}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
              {t.size}/10
            </div>
          </div>
          <div className="mt-2 text-[11px] text-[#71717A]">
            <span className="text-[#A5B4FC] font-mono uppercase tracking-widest text-[9px]">CAPTAIN</span> @{t.captain?.username || "—"}
          </div>
          {t.deputies?.length > 0 && (
            <div className="mt-1 text-[11px] text-[#71717A]">
              <span className="text-[#FBBF24] font-mono uppercase tracking-widest text-[9px]">DEPUTIES</span> {t.deputies.map((d) => `@${d.username}`).join(", ")}
            </div>
          )}
          {t.players?.length > 0 && (
            <div className="mt-1 text-[11px] text-[#71717A]">
              <span className="font-mono uppercase tracking-widest text-[9px]">PLAYERS</span> {t.players.map((p) => `@${p.username}`).join(", ")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function FixturesTable({ fixtures, onOverride, onReschedule }) {
  const grouped = useMemo(() => {
    const m = new Map();
    for (const f of (fixtures || [])) {
      const k = f.matchday;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [fixtures]);

  if (!fixtures?.length) return <Panel className="font-mono text-[#71717A] text-sm">[ NO FIXTURES ]</Panel>;

  return (
    <div className="space-y-4" data-testid="admin-proclubs-fixtures-table">
      {grouped.map(([md, list]) => (
        <div key={md}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] mb-2">
            // Matchday {md}
          </div>
          <div className="space-y-1.5">
            {list.map((f) => <FixtureRow key={f.id} f={f} onOverride={onOverride} onReschedule={onReschedule} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function FixtureRow({ f, onOverride, onReschedule }) {
  const statusColor = {
    scheduled:        "#A1A1AA",
    awaiting_scores:  "#F59E0B",
    confirmed:        "#00FF87",
    disputed:         "#E11D48",
  }[f.status] || "#A1A1AA";
  const score = f.status === "confirmed" || f.admin_override
    ? `${f.home_score ?? "-"} – ${f.away_score ?? "-"}`
    : "vs";
  return (
    <div
      className="bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#1F1F2A] rounded-xl p-3 flex items-center gap-3"
      data-testid={`admin-proclubs-fixture-${f.id}`}
    >
      <span
        className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: `${statusColor}1A`, color: statusColor, border: `1px solid ${statusColor}55` }}
      >
        {f.status.replace(/_/g, " ")}
      </span>
      <div className="flex-1 min-w-0 text-sm font-mono text-[#09090B] dark:text-white truncate">
        {f.home_team?.crest_emoji || "⚽"} {f.home_team?.name || "TBD"} <span className="text-[#00FF87] font-bold mx-2">{score}</span> {f.away_team?.crest_emoji || "⚽"} {f.away_team?.name || "TBD"}
      </div>
      <div className="text-[10px] font-mono text-[#71717A] hidden sm:block flex-shrink-0">
        {(f.scheduled_at || "").slice(0, 16).replace("T", " ")}
      </div>
      {f.admin_override && (
        <span className="text-[9px] font-bold uppercase tracking-widest text-[#FBBF24]" title="Admin override applied">
          OVR
        </span>
      )}
      <button
        type="button"
        onClick={() => onReschedule?.(f)}
        data-testid={`admin-proclubs-fixture-${f.id}-reschedule`}
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] hover:text-white border border-[#A5B4FC]/40 hover:border-[#A5B4FC] rounded-full px-2 py-1 transition-colors"
      >
        <CalendarClock className="w-3 h-3" /> RSC
      </button>
      <button
        type="button"
        onClick={() => onOverride?.(f)}
        data-testid={`admin-proclubs-fixture-${f.id}-override`}
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FBBF24] hover:text-[#FFD700] border border-[#FBBF24]/40 hover:border-[#FFD700] rounded-full px-2 py-1 transition-colors"
      >
        <Gavel className="w-3 h-3" /> Override
      </button>
    </div>
  );
}

/* =========================================================================
   OVERRIDE / RESCHEDULE MODALS
   ========================================================================= */
function OverrideModal({ fixture, onClose, onApplied }) {
  const [homeScore, setHomeScore] = useState(fixture.home_score ?? fixture.home_submission?.my_score ?? 0);
  const [awayScore, setAwayScore] = useState(fixture.away_score ?? fixture.away_submission?.my_score ?? 0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/proclubs/admin/fixtures/${fixture.id}/override`, {
        home_score: Number(homeScore),
        away_score: Number(awayScore),
        note: note.trim() || null,
      });
      toast.success("Override applied.");
      onApplied?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="admin-proclubs-override-modal">
      <div className="bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#09090B] dark:text-white flex items-center gap-2">
            <Gavel className="w-4 h-4 text-[#FBBF24]" /> Override fixture
          </h3>
          <button type="button" onClick={onClose} data-testid="admin-proclubs-override-close" className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#1F1F2A]">
            <X className="w-3.5 h-3.5 text-[#A1A1AA]" />
          </button>
        </div>
        <div className="text-xs text-[#71717A]">
          {fixture.home_team?.name || "Home"} vs {fixture.away_team?.name || "Away"} · MD {fixture.matchday}
        </div>
        {(fixture.home_submission || fixture.away_submission) && (
          <div className="space-y-1 text-[11px] font-mono uppercase tracking-widest text-[#71717A] bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] rounded-xl p-3">
            {fixture.home_submission && (
              <div>HOME submitted: {fixture.home_submission.my_score}–{fixture.home_submission.opp_score}</div>
            )}
            {fixture.away_submission && (
              <div>AWAY submitted: {fixture.away_submission.opp_score}–{fixture.away_submission.my_score}</div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <NumField label={`${fixture.home_team?.name || "Home"} score`} testId="admin-proclubs-override-home" value={homeScore} setValue={setHomeScore} />
          <NumField label={`${fixture.away_team?.name || "Away"} score`} testId="admin-proclubs-override-away" value={awayScore} setValue={setAwayScore} />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">Reason (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="admin-proclubs-override-note"
            className="w-full mt-1 rounded-xl bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] px-3 py-2 text-sm text-[#09090B] dark:text-white"
            rows={2}
            maxLength={500}
            placeholder="e.g. Both screenshots agree on 3-1; mismatch was a typo."
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="admin-proclubs-override-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0F0F18] font-bold text-sm py-2.5 hover:opacity-95 disabled:opacity-50 transition-opacity"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply override
        </button>
      </div>
    </div>
  );
}

function RescheduleModal({ fixture, onClose, onApplied }) {
  const initial = (fixture.scheduled_at || "").slice(0, 16);
  const [when, setWhen] = useState(initial);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!when) {
      toast.error("Pick a new date / time.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/proclubs/admin/fixtures/${fixture.id}/reschedule`, {
        new_at: new Date(when).toISOString(),
        note: note.trim() || null,
      });
      toast.success("Reschedule applied.");
      onApplied?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="admin-proclubs-reschedule-modal">
      <div className="bg-white dark:bg-[#12121C] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#09090B] dark:text-white flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#A5B4FC]" /> Reschedule fixture
          </h3>
          <button type="button" onClick={onClose} data-testid="admin-proclubs-reschedule-close" className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#1F1F2A]">
            <X className="w-3.5 h-3.5 text-[#A1A1AA]" />
          </button>
        </div>
        <div className="text-xs text-[#71717A]">
          {fixture.home_team?.name || "Home"} vs {fixture.away_team?.name || "Away"} · MD {fixture.matchday}
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">New date & time (UTC)</label>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            data-testid="admin-proclubs-reschedule-when"
            className="w-full mt-1 rounded-xl bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] px-3 py-2 text-sm text-[#09090B] dark:text-white"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">Reason (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="admin-proclubs-reschedule-note"
            className="w-full mt-1 rounded-xl bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] px-3 py-2 text-sm text-[#09090B] dark:text-white"
            rows={2}
            maxLength={500}
            placeholder="e.g. Captains agreed in WA chat to move to Sat 21:00 UTC."
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="admin-proclubs-reschedule-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm py-2.5 hover:opacity-95 disabled:opacity-50 transition-opacity"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply reschedule
        </button>
      </div>
    </div>
  );
}

function NumField({ label, value, setValue, testId }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{label}</label>
      <input
        type="number"
        min="0"
        max="99"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={testId}
        className="w-full mt-1 rounded-xl bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] px-3 py-2 text-lg font-bold text-[#09090B] dark:text-white text-center tabular-nums"
      />
    </div>
  );
}

/* =========================================================================
   DISPUTES VIEW — cross-league disputed fixtures
   ========================================================================= */
function DisputesView() {
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [overrideFx, setOverrideFx] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const { data } = await api.get("/proclubs/admin/fixtures/disputed");
      setItems(data?.fixtures || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setRefreshing(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (items === null) return <TactLoader />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-[#71717A]">
          {items.length} disputed fixture{items.length !== 1 ? "s" : ""}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="admin-proclubs-disputes-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#1F1F2A] text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 text-[#A1A1AA] hover:text-white transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>
      {items.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO DISPUTED PRO CLUBS FIXTURES. ALL CLEAR, COMMANDER. ]</Panel>
      ) : (
        <div className="space-y-2" data-testid="admin-proclubs-disputes-list">
          {items.map((f) => (
            <DisputeRow key={f.id} f={f} onOverride={() => setOverrideFx(f)} />
          ))}
        </div>
      )}
      {overrideFx && (
        <OverrideModal
          fixture={overrideFx}
          onClose={() => setOverrideFx(null)}
          onApplied={async () => { setOverrideFx(null); await load(true); }}
        />
      )}
    </div>
  );
}

function DisputeRow({ f, onOverride }) {
  return (
    <div className="bg-[#E11D48]/5 dark:bg-[#E11D48]/10 border border-[#E11D48]/30 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-proclubs-dispute-${f.id}`}>
      <AlertTriangle className="w-4 h-4 text-[#E11D48] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-mono text-[#09090B] dark:text-white truncate">
          {f.home_team?.crest_emoji || "⚽"} {f.home_team?.name || "Home"} <span className="text-[#71717A] mx-1">vs</span> {f.away_team?.crest_emoji || "⚽"} {f.away_team?.name || "Away"}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mt-0.5">
          MD {f.matchday} · {(f.scheduled_at || "").slice(0, 10)} · home {f.home_submission?.my_score ?? "–"}–{f.home_submission?.opp_score ?? "–"}  vs  away {f.away_submission?.opp_score ?? "–"}–{f.away_submission?.my_score ?? "–"}
        </div>
      </div>
      <button
        type="button"
        onClick={onOverride}
        data-testid={`admin-proclubs-dispute-${f.id}-resolve`}
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FBBF24] hover:text-[#FFD700] border border-[#FBBF24]/40 hover:border-[#FFD700] rounded-full px-3 py-1.5 transition-colors"
      >
        <Gavel className="w-3 h-3" /> Resolve
      </button>
    </div>
  );
}

/* =========================================================================
   FIXTURES VIEW — cross-league fixture browser (filter by status)
   ========================================================================= */
function FixturesView() {
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState("");
  const [status, setStatus] = useState("");
  const [fixtures, setFixtures] = useState(null);
  const [overrideFx, setOverrideFx] = useState(null);
  const [rescheduleFx, setRescheduleFx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/proclubs/admin/leagues");
        setLeagues(data?.leagues || []);
      } catch { /* noop */ }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!leagueId) {
      setFixtures([]);
      return;
    }
    try {
      const { data } = await api.get(`/proclubs/admin/leagues/${leagueId}`);
      setFixtures(data?.fixtures || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [leagueId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!fixtures) return null;
    if (!status) return fixtures;
    return fixtures.filter((f) => f.status === status);
  }, [fixtures, status]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={leagueId}
          onChange={(e) => setLeagueId(e.target.value)}
          data-testid="admin-proclubs-fixtures-league"
          className="text-xs font-mono bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] rounded-full px-3 py-2 text-[#09090B] dark:text-white"
        >
          <option value="">Pick a league…</option>
          {leagues.map((L) => (
            <option key={L.id} value={L.id}>
              {(L.pool || "mobile").toUpperCase()} · Season {L.season_no} · {L.status}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="admin-proclubs-fixtures-status"
          className="text-xs font-mono bg-[#F4F4F5] dark:bg-[#0F0F18] border border-[#E4E4E7] dark:border-[#1F1F2A] rounded-full px-3 py-2 text-[#09090B] dark:text-white"
        >
          <option value="">All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="awaiting_scores">Awaiting</option>
          <option value="confirmed">Confirmed</option>
          <option value="disputed">Disputed</option>
        </select>
      </div>

      {filtered === null ? (
        <TactLoader />
      ) : filtered.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ {leagueId ? "NO FIXTURES MATCH FILTER" : "PICK A LEAGUE TO BEGIN"} ]</Panel>
      ) : (
        <FixturesTable
          fixtures={filtered}
          onOverride={(f) => setOverrideFx(f)}
          onReschedule={(f) => setRescheduleFx(f)}
        />
      )}

      {overrideFx && (
        <OverrideModal
          fixture={overrideFx}
          onClose={() => setOverrideFx(null)}
          onApplied={async () => { setOverrideFx(null); await load(); }}
        />
      )}
      {rescheduleFx && (
        <RescheduleModal
          fixture={rescheduleFx}
          onClose={() => setRescheduleFx(null)}
          onApplied={async () => { setRescheduleFx(null); await load(); }}
        />
      )}
    </div>
  );
}
